import {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";
import {Box, BoxProps, LinearProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {IMarkedQuizAnswers, IQuiz, IQuizAnswer} from "@buildwitt/shapeshift-types";
import {App2NotificationContext, App2NotificationSeverity} from "@/app2/shared/contexts/App2NotificationContext";
import {
	useSubmitInlineQuiz,
	useGetInlineQuizQuestions,
	useGetInlineQuizLatestResult
} from "@/app2/shared/data";
import {MiniQuizzerQuestions} from "./MiniQuizzerQuestions";
import {MiniQuizzerComplete} from "./MiniQuizzerComplete";
import {convertToMarkedAnswers} from "./convertToMarkedAnswers";
import {
	mapApiQuestionsForDisplay,
	mapSubmissionToCanonicalValues
} from "./inlineQuizQuestions";

const useStyles = makeStyles<{ disabled?: boolean }>()((theme, {disabled}) => ({
	miniQuizzer: {
		opacity: disabled ? 0.5 : 1,
		[theme.breakpoints.down("sm")]: {
			paddingTop: "20px"
		}
	},
	innerContainer: {
		pointerEvents: disabled ? "none" : "auto"
	},
	quizStartSummary: {
		padding: 0,
		maxWidth: "none",
		textAlign: "left",
		"& [class*='centerTile']": {
			padding: 0
		}
	}
}));

interface IPropsCustom {
	quiz: IQuiz;
	enrollmentId: number | null;
	disabled?: boolean;
	disabledMessage?: string;
	autoStart?: boolean;
	onQuizResult?: (e: IMarkedQuizAnswers) => void;
	onComplete: (result: IMarkedQuizAnswers) => void;
}

type IProps = IPropsCustom & BoxProps;

export const MiniQuizzer: FC<IProps> = ({
	quiz,
	enrollmentId,
	onComplete,
	onQuizResult,
	autoStart,
	disabled,
	disabledMessage,
	className,
	...props
}) => {
	const {classes, cx} = useStyles({disabled});
	const [navState, setNavState] = useState<"summary" | "active" | "complete">(autoStart ? "active" : "summary");
	const [quizResult, setQuizResult] = useState<IMarkedQuizAnswers>();
	const {notify} = useContext(App2NotificationContext);
	const [{locale}] = useContext(LocaleContext);
	const resolvedLocale = resolveLocale(locale);

	const {data: questionsData, error: questionsError, isLoading: questionsLoading} = useGetInlineQuizQuestions(
		enrollmentId,
		{locale: resolvedLocale}
	);

	const apiQuestions = questionsData?.questions ?? [];
	const displayQuestions = useMemo(
		() => mapApiQuestionsForDisplay(apiQuestions, resolvedLocale),
		[apiQuestions, resolvedLocale]
	);
	const {data: prevQuizResult} = useGetInlineQuizLatestResult(enrollmentId);
	const markQuizMutation = useSubmitInlineQuiz(enrollmentId);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Reset when switching lessons (same component instance, new enrollment).
	useEffect(() => {
		setQuizResult(undefined);
		setNavState(autoStart ? "active" : "summary");
		setIsSubmitting(false);
	}, [enrollmentId, autoStart]);

	const markAnswersActionOverride = useCallback(
		(answers: IQuizAnswer[]) => {
			if (isSubmitting) return true;
			setIsSubmitting(true);
			const formattedAnswers = mapSubmissionToCanonicalValues(apiQuestions, answers, resolvedLocale);

			markQuizMutation.mutate(
				{answers: formattedAnswers as any},
				{
					onSuccess: (data) => {
						const markedAnswers: IMarkedQuizAnswers = {
							pass: data.passed,
							correct: data.correctAnswers,
							incorrect: data.totalQuestions - data.correctAnswers,
							requiredScore: data.passingScore,
							markedAnswers: data.markedAnswers.map((ma) => ({
								questionId: ma.questionId,
								userAnswer: ma.userAnswer as any,
								correctAnswer: ma.correctAnswer as any,
								correct: ma.isCorrect
							})) as any
						};
						setQuizResult(markedAnswers);
						setIsSubmitting(false);
					},
					onError: (error: any) => {
						setIsSubmitting(false);
						notify(
							"Please try again",
							error?.response?.data?.message || "Failed to mark quiz",
							App2NotificationSeverity.error,
							5000
						);
					}
				}
			);

			return true;
		},
		[markQuizMutation, notify, isSubmitting, apiQuestions, resolvedLocale]
	);

	useEffect(() => {
		if (prevQuizResult?.completed) {
			const convertedResult: IMarkedQuizAnswers = {
				pass: prevQuizResult.pass,
				correct: prevQuizResult.correct,
				incorrect: prevQuizResult.incorrect,
				requiredScore: prevQuizResult.requiredScore,
				markedAnswers: prevQuizResult.markedAnswers as any
			};
			setQuizResult(convertedResult);
		} else {
			setQuizResult(undefined);
		}
	}, [prevQuizResult, enrollmentId]);

	useEffect(() => {
		if (quizResult) {
			onQuizResult?.(quizResult);
		}
	}, [quizResult, onQuizResult, enrollmentId]);

	useEffect(() => {
		if (quizResult) {
			setNavState("complete");
			if (quizResult.pass) onComplete(quizResult);
		}
	}, [quizResult, onComplete]);

	const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
		if (disabled) {
			notify(disabledMessage ?? "quiz is disabled", "", App2NotificationSeverity.warning, 5000, false);
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const displayMarkedAnswers = useMemo(() => {
		if (
			!quizResult ||
			!questionsData?.questions ||
			questionsData.questions.length === 0 ||
			!quizResult.markedAnswers?.length
		) {
			return null;
		}
		return convertToMarkedAnswers(
			displayQuestions,
			quizResult.markedAnswers as any,
			{
				correct: quizResult.correct,
				incorrect: quizResult.incorrect,
				requiredScore: quizResult.requiredScore,
				pass: quizResult.pass
			},
			{apiQuestions, locale: resolvedLocale}
		);
	}, [quizResult, displayQuestions, apiQuestions, resolvedLocale]);

	if (questionsError?.response?.status === 400) {
		return <></>;
	}

	return (
		<Box onClickCapture={(e) => handleClick(e)} className={cx(classes.miniQuizzer, className)} {...props}>
			<Box className={classes.innerContainer}>
				{navState === "active" && (questionsLoading || isSubmitting || markQuizMutation.isPending) && <LinearProgress />}
				{navState === "active" &&
					!questionsLoading &&
					questionsData?.questions &&
					questionsData.questions.length > 0 && (
						<Box sx={(isSubmitting || markQuizMutation.isPending) ? {pointerEvents: "none", opacity: 0.5} : undefined}>
							<MiniQuizzerQuestions
								questions={displayQuestions}
								onSubmit={markAnswersActionOverride}
							/>
						</Box>
					)}
				{navState === "complete" && displayMarkedAnswers && (
					<MiniQuizzerComplete
						quiz={quiz}
						markedAnswers={displayMarkedAnswers}
						onRetake={() => setNavState("active")}
					/>
				)}
			</Box>
		</Box>
	);
};
